import type { KeyResult, Objective, Task, WorkflowColumn } from '@portfolio-pm/shared';
import { X } from 'lucide-react';
import { useState } from 'react';
import type { DocStore } from '../../store/doc-store.ts';
import {
  fieldClass,
  iconButtonClass,
  innerCardClass,
  labelClass,
  primaryButtonClass,
  secondaryButtonClass,
  selectClass,
  shellCardClass,
} from './okr-ui.tsx';

/**
 * Detail pane for an unlinked task in the OKR list view, with the
 * objective → KR cascade link selector (REQ-OKR-006).
 * Extracted from OkrWorkspace.tsx.
 */
export function OkrTaskDetail({
  task,
  projectObjectives,
  keyResultsByObjective,
  workflowColumns,
  linkError,
  onLink,
  onUnlink,
  onClose,
  store,
}: {
  task: Task;
  projectObjectives: Objective[];
  keyResultsByObjective: Map<string, KeyResult[]>;
  workflowColumns: WorkflowColumn[];
  linkError: string | undefined;
  onLink(krId: string): void;
  onUnlink(): void;
  onClose(): void;
  store: DocStore;
}): JSX.Element {
  const [linkObjId, setLinkObjId] = useState('');
  const [linkKrId, setLinkKrId] = useState('');

  const krsForObj = linkObjId ? (keyResultsByObjective.get(linkObjId) ?? []) : [];

  const priorityLabel: Record<number, string> = { 0: 'None', 1: 'P1', 2: 'P2', 3: 'P3' };
  const workflowLabel =
    workflowColumns.find((c) => c.id === task.workflowState)?.name ?? task.workflowState;

  const formatDate = (ms: number | undefined): string => {
    if (ms === undefined) return '—';
    return new Date(ms).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className={`${shellCardClass} space-y-4 p-4`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className={labelClass}>Unlinked task</p>
          <input
            key={`${task.id}-title`}
            defaultValue={task.title}
            onBlur={(e) => store.renameTask(task.id, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
            className={`${fieldClass} mt-1 font-medium`}
          />
        </div>
        <button
          type="button"
          aria-label="Close task detail"
          onClick={onClose}
          className={iconButtonClass}
        >
          <X aria-hidden="true" size={13} />
        </button>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <div className={`${innerCardClass} p-2.5`}>
          <p className={labelClass}>Status</p>
          <p className="mt-1 text-xs font-medium text-[color:var(--app-text)]">
            {task.completedAt ? 'Completed' : workflowLabel}
          </p>
        </div>
        <div className={`${innerCardClass} p-2.5`}>
          <p className={labelClass}>Priority</p>
          <p className="mt-1 text-xs font-medium text-[color:var(--app-text)]">
            {priorityLabel[task.priority] ?? 'None'}
          </p>
        </div>
        <div className={`${innerCardClass} p-2.5`}>
          <p className={labelClass}>Due</p>
          <p className="mt-1 text-xs text-[color:var(--app-text-muted)]">
            {formatDate(task.dueAt)}
          </p>
        </div>
        <div className={`${innerCardClass} p-2.5`}>
          <p className={labelClass}>Scheduled</p>
          <p className="mt-1 text-xs text-[color:var(--app-text-muted)]">
            {formatDate(task.scheduledAt)}
          </p>
        </div>
      </div>

      {task.tags.length > 0 && (
        <div className={`${innerCardClass} p-2.5`}>
          <p className={labelClass}>Tags</p>
          <p className="mt-1 text-xs text-[color:var(--app-text-muted)]">{task.tags.join(', ')}</p>
        </div>
      )}

      {task.notes && (
        <div className={`${innerCardClass} p-2.5`}>
          <p className={labelClass}>Notes</p>
          <p className="mt-1 text-xs text-[color:var(--app-text-muted)] line-clamp-4">
            {task.notes}
          </p>
        </div>
      )}

      <div className="space-y-2 border-t border-[color:var(--app-border)] pt-4">
        <p className={labelClass}>Link to objective</p>
        <select
          value={linkObjId}
          onChange={(e) => {
            setLinkObjId(e.target.value);
            setLinkKrId('');
          }}
          className={selectClass}
        >
          <option value="">Select objective...</option>
          {projectObjectives.map((o) => (
            <option key={o.id} value={o.id}>
              {o.title}
            </option>
          ))}
        </select>

        {linkObjId && (
          <select
            value={linkKrId}
            onChange={(e) => setLinkKrId(e.target.value)}
            className={selectClass}
          >
            <option value="">Select key result...</option>
            {krsForObj.map((kr) => (
              <option key={kr.id} value={kr.id}>
                {kr.title}
              </option>
            ))}
          </select>
        )}

        {linkError && <p className="text-xs text-[color:var(--app-danger)]">{linkError}</p>}

        <div className="flex justify-end gap-2">
          {task.krId && (
            <button type="button" onClick={onUnlink} className={secondaryButtonClass}>
              Unlink
            </button>
          )}
          <button
            type="button"
            disabled={!linkKrId}
            onClick={() => {
              if (linkKrId) onLink(linkKrId);
            }}
            className={primaryButtonClass}
          >
            Link
          </button>
        </div>
      </div>
    </div>
  );
}
